import { Component, inject, OnInit } from '@angular/core';
import { CalldiusStepItem, CallidusStepperComponent } from '@shared/components/callidus-stepper/callidus-stepper.component';
import { CustomerDataComponent } from '@shared/components/customer-data/customer-data.component';
import { DataSubmittedComponent } from '@shared/components/data-submitted/data-submitted.component';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { unsubscribeMixin } from '@shared/mixin/unsubscribe.mixin';
import { CharterKautionsversicherungComponent } from '../../components/charter-kautionsversicherung/charter-kautionsversicherung.component';
import { createBvsCharterKautionsversicherungForm } from '../../factories/bvs-charter-kautionsversicherung-form.factory';
import { BvsCharterKautionsversicherungFormModel } from '../../models/forms/bvs-charter-kautionsversicherung-form.model';
import { BrokernetLanguageType, CallidusCharterVersicherungspraemieCalcDto, PublicAddressCreateDto, PublicCallidusCharterContractCreateDto, PublicPersonCreateDto } from '@backend/models';
import { takeUntil } from 'rxjs';
import { CharterkautionStateService } from '@shared/services/charterkaution-state.service';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';

@Component({
  selector: 'app-charter-kautionsversicherung-page',
  imports: [
    CallidusStepperComponent,
    CustomerDataComponent,
    DataSubmittedComponent,
    CharterKautionsversicherungComponent
  ],
  templateUrl: './charter-kautionsversicherung-page.component.html',
  styleUrl: './charter-kautionsversicherung-page.component.scss'
})
export class CharterKautionsversicherungPageComponent extends unsubscribeMixin() implements OnInit {
  private readonly charterkautionStateService = inject(CharterkautionStateService);
  private readonly router = inject(Router);
  private readonly urlParamProviderService = inject(UrlParamProviderService);

  stepItems: CalldiusStepItem[] = [
    { id: '0', label: 'bvs.calculator.menu.infos', iconUrl: '/assets/icons/info-circle.svg' },
    { id: '1', label: 'bvs.calculator.menu.finish', iconUrl: '/assets/icons/health-shield.svg' }
  ];

  curentNavigationStep: BvsCharterKautionsversicherungNavigationEnum = BvsCharterKautionsversicherungNavigationEnum.Base;
  form!: FormGroup<BvsCharterKautionsversicherungFormModel>;

  protected readonly SkipperHaftpflichtNavigationEnum = BvsCharterKautionsversicherungNavigationEnum;

  private stepOrder: BvsCharterKautionsversicherungNavigationEnum[] = [
    BvsCharterKautionsversicherungNavigationEnum.Base,
    BvsCharterKautionsversicherungNavigationEnum.Customer,
    BvsCharterKautionsversicherungNavigationEnum.Done
  ];

  ngOnInit(): void {
    this.form = createBvsCharterKautionsversicherungForm();
  }

  getCurrentPosition(): number {
    return this.stepOrder.indexOf(this.curentNavigationStep);
  }

  moveToNextStep(): void {
    this.curentNavigationStep++;
  }

  moveToPreviousStep(): void {
    if (this.curentNavigationStep === BvsCharterKautionsversicherungNavigationEnum.Base) {
      void this.router.navigate(['/']);
    }

    this.curentNavigationStep--;
  }

  submitData(): void {
    const personData = this.form.controls.customerData.controls.person.getRawValue();
    const legalChecks = this.form.controls.customerData.controls.legalChecks.controls;
    const formData = this.form.controls;

    const query = this.urlParamProviderService.getQueryData();
    const isLinked = !!query?.customerId && !!query?.personId;

    const onboardtingDto: PublicCallidusCharterContractCreateDto = {
      callidusCharterVersicherungspraemieCalcDto: {
        boatType: formData.boatType.value,
        kautionsBetragInEur: formData.kautionsBetragInEur.value,
        yachtLengthInFeet: formData.yachtLengthInFeet.value
      } as CallidusCharterVersicherungspraemieCalcDto,

      publicLegalCheck: {
        hatElektronischeKommunikationAkzeptiert: legalChecks.isContactConsent.value ?? false,
        hatDsgvoAkzeptiert: legalChecks.isGdprAccepted.value ?? false,
        hatAvbAkzeptiert: false,
        hatIpidAkzeptiert: false,
        hatSepaEinzugAkzeptiert: false,
        hatWiderrufsrechtAkzeptiert: false,
        hatWuenscheUndBeduerfnisseAkzeptiert: false
      },

      ...(isLinked
        ? { publicLinkCustomerDto: { customerId: query!.customerId!, personId: query!.personId! } }
        : {
          publicPerson: {
            titlePrefix: personData.titlePrefix,
            titleSuffix: personData.titleSuffix,
            firstname: personData.firstname,
            lastname: personData.lastname,
            birthdate: personData.birthdate,
            gender: personData.gender,
            brokernetLanguageType: localStorage.getItem('app-language') === BrokernetLanguageType.En ? BrokernetLanguageType.En : BrokernetLanguageType.De,
            publicContactInfoCreateDto: {
              email: personData.email,
              phone: personData.phone
            },
            publicAddressCreateDto: this.form.controls.customerData.controls.address.getRawValue() as PublicAddressCreateDto
          } as PublicPersonCreateDto
        }),

      isCharterFolgeschadenVersicherungSelected: formData.isCharterFolgeschadenVersicherungSelected.value,
      anmerkungCustomer: this.form.controls.customerData.controls.anmerkungCustomer.value
    } as PublicCallidusCharterContractCreateDto;

    this.charterkautionStateService.submitCharterOnboarding(onboardtingDto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.curentNavigationStep = BvsCharterKautionsversicherungNavigationEnum.Done;
        },
        error: (error) => {
          console.error('Error submitting onboarding:', error);
        }
      });
  }
}

export enum BvsCharterKautionsversicherungNavigationEnum {
  Base,
  Customer,
  Done
}
